import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { integrationsApi } from 'apis/integrations';
import { useServerStorage } from 'utils/useServerStorage';

import { CardSection } from '../../components/cardSection/cardSection';
import { useCardSectionVisibility } from '../../hooks/useCardSectionVisibility';
import { useCustomizationSnapshot } from '../../hooks/useCustomizationSnapshot';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import { reorderNumeric } from '../../utils/reorder';
import { CustomCardsItem } from '../customCardsItem';

export type CustomCardsProps = {
  isMinimized?: boolean;
  csid?: number | null;
  /**
   * Section order index, if not provided - section is not displayed, only
   * items. Done for backward compatibility, to display custom integrations in
   * Integrations section. Should be made required once
   * `conversation-right-sidebar-visibility` flag is dropped.
   */
  sectionIndex?: number;
};

const type = Symbol('customIntegrationCard');

export const CustomCards = ({
  isMinimized = false,
  sectionIndex,
  csid,
}: CustomCardsProps): JSX.Element | null => {
  const { hiddenItems } = useCardSectionVisibility();
  const sidebarMode = useSidebarMode();
  const { customCards, isFetching, isError, isSuccess } =
    integrationsApi.useReadCustomCardIntegrationsSanitizedQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        customCards: data?.data.custom_cards || [],
      }),
    });

  const storage = useServerStorage('user');

  const sortedCards = useMemo(() => {
    const cardOrder =
      storage.data.conversation?.sidebarCustomIntegrationOrder ?? {};

    return customCards
      .filter(
        (card) => sidebarMode === 'customization' || !hiddenItems.has(card.id),
      )
      .sort(
        (a, b) =>
          (cardOrder[a.id] ?? -Infinity) - (cardOrder[b.id] ?? -Infinity) ||
          a.title.localeCompare(b.title),
      );
  }, [
    customCards,
    hiddenItems,
    sidebarMode,
    storage.data.conversation?.sidebarCustomIntegrationOrder,
  ]);

  const { items: preparedCards, reorder: reorderCards } =
    useCustomizationSnapshot(sortedCards, 'id');

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      reorderCards(dragIndex, hoverIndex);

      storage.set({
        conversation: (draft) => {
          draft.sidebarCustomIntegrationOrder = reorderNumeric(
            preparedCards,
            dragIndex,
            hoverIndex,
          );
        },
      });

      ampli.conversationRightSidebarItemReordered({
        sidebarItemType: 'card',
      });
    },
    [storage, preparedCards, reorderCards],
  );

  if (isError) {
    return (
      <FlexBox justifyContent="center" padding="400">
        <Text.Paragraph>
          Something went wrong fetching custom cards.
        </Text.Paragraph>
      </FlexBox>
    );
  }

  if (isSuccess && !preparedCards.length) {
    return null;
  }

  const cardsOrSpinner = isFetching ? (
    <FlexBox justifyContent="center" padding="400">
      <Spinner />
    </FlexBox>
  ) : (
    preparedCards.map((card, idx) => (
      <CustomCardsItem
        key={card.id}
        card={card}
        isMinimized={isMinimized}
        csid={csid}
        index={idx}
        type={type}
        onMove={handleMove}
      />
    ))
  );

  return typeof sectionIndex === 'number' ? (
    <CardSection
      sectionId="customIntegration"
      index={sectionIndex}
      isMinimized={isMinimized}
    >
      {cardsOrSpinner}
    </CardSection>
  ) : (
    <>{cardsOrSpinner}</>
  );
};
